import { useState, CSSProperties, useEffect } from 'react'
import { PLACEMENT, StatefulPopover } from 'baseui/popover'
import { Slider } from 'baseui/slider'
import { Block } from 'baseui/block'
import { Input } from 'baseui/input'
import { ThemeProvider } from 'baseui'
import { lightTheme } from '@/customTheme'
import { useTranslation } from 'react-i18next'
import { Z_INDEX } from '@/constants/z-index'
import AutoScroll from '@/components/AutoScroll'

/** UI-value distance within which a drag/click snaps onto `snapValue`. */
const SNAP_THRESHOLD = 3

function SliderCustom({
  icon,
  name,
  listValues,
  minValue,
  maxValue,
  value,
  step,
  onChangeValue,
  onFinalChangeValue,
  logicMaximum,
  logicMinimum,
  snapValue,
  fillMode = 'edge',
  suffix,
  style,
  disabled = false,
  isMixed = false,
}: {
  icon?: JSX.Element
  name: string
  listValues: number[]
  minValue: number
  maxValue: number
  value: number
  step?: number
  onChangeValue: Function
  onFinalChangeValue?: Function
  logicMaximum?: number
  logicMinimum?: number
  /** UI value (e.g. 0) that magnetically snaps drags/clicks near it. */
  snapValue?: number
  /** 'edge' (default) fills from the track start; 'center' fills between `snapValue` and the thumb, growing either direction. */
  fillMode?: 'edge' | 'center'
  /** Unit appended to the value box while it isn't being edited (e.g. "%"). Widens the box to fit. */
  suffix?: string
  style?: CSSProperties
  disabled?: boolean
  /** Shows an em dash until the user starts editing a mixed selection. */
  isMixed?: boolean
}) {
  const [inputValue, setInputValue] = useState(minValue)
  const [showDropDown, setShowDropDown] = useState(true)
  // The suffix is display-only: it's dropped while editing so the digits-only
  // onChange guard b elow still accepts every keystroke.
  const [editingValue, setEditingValue] = useState(false)

  const applySnap = (uiValue: number, previousValue: number): number => {
    if (snapValue === undefined) {
      return uiValue
    }
    const movingAway = Math.abs(uiValue - snapValue) > Math.abs(previousValue - snapValue)
    if (movingAway) {
      return uiValue
    }
    return Math.abs(uiValue - snapValue) <= SNAP_THRESHOLD ? snapValue : uiValue
  }

  const handleUpdateValue = (uiValue, isFinal, isInput = false, shouldSnap = true) => {
    const numericValue = isInput
      ? (uiValue === '' || uiValue === '-' ? uiValue : Number(uiValue))
      : shouldSnap
      ? applySnap(Number(uiValue), inputValue)
      : Number(uiValue)
    setInputValue(numericValue)
    if(isInput) {
      return
    }
    let logicValue = convertUIToLogic(numericValue)
    onChangeValue(numericValue, logicValue)
    if (isFinal && onFinalChangeValue) {
      onFinalChangeValue(numericValue, logicValue)
    }
  }

  const convertUIToLogic = (uiValue: number): number => {
    if ([null, undefined].includes(logicMaximum && logicMinimum)) {
      return uiValue
    }

    let normalizedUIValue = (uiValue - minValue) / (maxValue - minValue)
    let normalizedLogicValue = normalizedUIValue * (logicMaximum - logicMinimum) + logicMinimum
    return normalizedLogicValue
  }

  const convertLogicToUI = (inputValue: number): number => {
    if ([null, undefined].includes(logicMaximum && logicMinimum)) {
      return inputValue // it means inputValue is actual uiValue
    }
    let logicValue = inputValue
    let normalizedLogicValue = (logicValue - logicMinimum) / (logicMaximum - logicMinimum)
    let normalizedUIValue = normalizedLogicValue * (maxValue - minValue) + minValue
    return Math.round(normalizedUIValue)
  }

  useEffect(() => {
    setInputValue(convertLogicToUI(value))
  },[value])

  const { t } = useTranslation()

  return (
    <ThemeProvider theme={lightTheme}>
      <div style={{ height: '62px', width: '100%', display: 'flex', gap: '12px', flexDirection: 'column', opacity: disabled ? 0.4 : 1, pointerEvents: disabled ? 'none' : undefined, ...style }} className="mt-50">
        <div
          style={{
            width: '100%',
            height: '26px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'start',
            gap: '6px',
          }}
        >
          {icon}
          <span
            style={{
              ...lightTheme.typography.Small14Semibold,
              color: style?.color || lightTheme.colors.text.text_black_title,
              marginTop: '1px',
              textTransform: 'capitalize',
            }}
          >
            {t(name)}
          </span>
          <StatefulPopover
            placement={PLACEMENT.bottomLeft}
            overrides={{
              Body: {
                style: ({ $theme }) => ({
                  marginTop: '4px',
                  left: '-2px',
                  zIndex: Z_INDEX.POPOVER_HIGH,
                }),
              },
            }}
            content={({ close }) =>
                <>
                {
                  listValues.length ? <Block
                  backgroundColor={'#ffffff'}
                  padding={'8px 4px'}
                  overrides={{
                    Block: {
                      style: {
                        display: showDropDown ? 'block' : 'none',
                        borderRadius: '8px',
                      },
                    },
                  }}
                >
                  <AutoScroll
                    style={{
                      maxHeight: '450px',
                      background: '#FFFFFF',
                      borderRadius: '8px',
                      paddingTop: 0,
                      paddingLeft: 0,
                      paddingRight: 0,
                      marginRight: 0,
                    }}
                  >
                    {listValues.map((size, index) => (
                      <Block
                        onClick={() => {
                          handleUpdateValue(size, true)
                          setInputValue(size)
                          close()
                        }}
                        $style={{
                          height: '32px',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          padding: '4px 12px',
                          borderRadius: '8px',
                          display: 'flex ',
                          justifyContent: 'center',
                          alignItems: 'center',
                          ':hover': {
                            background: '#F2F2F2',
                          },
                        }}
                        key={size}
                      >
                        {size}
                      </Block>
                    ))}
                  </AutoScroll>
                </Block> : null
                }
                </>
            }
          >
            <div style={{ marginLeft: 'auto' }}>
              <Input
                onKeyDown={e => {
                  if(e.key === 'Enter') {
                    if (Number.isNaN(Number(inputValue))) {
                      return
                    }
                    if(inputValue < minValue) {
                      handleUpdateValue(minValue, true, false, false)
                    }else if(inputValue > maxValue) {
                      handleUpdateValue(maxValue, true, false, false)
                    } else {
                      handleUpdateValue(inputValue, true, false, false)
                    }
                    setShowDropDown(false)
                  }
                }}
                value={isMixed && !editingValue ? '—' : suffix && !editingValue ? `${inputValue}${suffix}` : inputValue}
                disabled={disabled}
                onChange={e => {
                  const value = e.currentTarget.value
                  const isNumber = /^-?[0-9]*$/.test(value)
                  if (isNumber) {
                    handleUpdateValue(value, true, true)
                  }
                }}
                clearOnEscape
                min={minValue}
                max={maxValue}
                onFocus={e => {
                  setShowDropDown(true)
                  setEditingValue(true)
                  setTimeout(() => {
                    e.target.select()
                  }, 100)
                }}
                onBlur={() => setEditingValue(false)}
                overrides={{
                  Root: {
                    style: ({ $theme }) => ({
                      width: suffix ? '52px' : '44px',
                      height: '24px',
                      border: '0px',
                      padding: 0,
                      display: 'flex',
                      ':focus': {
                        border: '0px',
                      },
                    }),
                  },
                  Input: {
                    style: ({ $theme }) => ({
                      textAlign: 'center',
                      paddingLeft: 0,
                      paddingRight: 0,
                      background: '#F2F2F2',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: lightTheme.colors.blackGray,
                      ':hover': {
                        background: lightTheme.colors.grayScale100,
                      },
                      ':focus': {
                        background: '#FFFFFF',
                        border: '1px solid #666666 ',
                      },
                    }),
                  },
                }}
              />
            </div>
          </StatefulPopover>
        </div>
        <div
          style={{
            height: '24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '8px',
            position: 'relative',
          }}
        >
          <Slider
            disabled={disabled}
            min={minValue}
            max={maxValue}
            value={[inputValue > maxValue ? maxValue : inputValue]}
            step={step}
            onChange={({ value }) => {
              handleUpdateValue(value[0], false)
            }}
            onFinalChange={({ value }) => {
              handleUpdateValue(value[0], true)
            }}
            overrides={{
              Root: {
                style: ({ $theme }) => ({
                  // height: '24px',
                }),
              },
              Track: {
                style: ({ $theme }) => ({
                  paddingLeft: '10px',
                  paddingRight: '10px',
                }),
              },
              InnerThumb: ({ $value, $thumbIndex }) => <>{}</>,
              ThumbValue: () => null,
              Thumb: {
                style: () => ({
                  height: '20px',
                  width: '20px',
                  borderRadius: '50%',
                  boxShadow: '0px 0px 6px rgba(0, 0, 0, 0.3)',
                  backgroundColor: '#fff',
                }),
              },
              InnerTrack: {
                style: ({ $theme }) => {
                  if (fillMode === 'center' && snapValue !== undefined) {
                    const pivotPercent = ((snapValue - minValue) / (maxValue - minValue)) * 100
                    const valuePercent = ((inputValue - minValue) / (maxValue - minValue)) * 100
                    const start = Math.min(pivotPercent, valuePercent)
                    const end = Math.max(pivotPercent, valuePercent)
                    return {
                      // @ts-ignore
                      background: `linear-gradient(to right, ${$theme.colors.grayScale100} 0%, ${
                        // @ts-ignore
                        $theme.colors.grayScale100
                        // @ts-ignore
                      } ${start}%, ${$theme.colors.blackGray} ${start}%, ${
                        // @ts-ignore
                        $theme.colors.blackGray
                        // @ts-ignore
                      } ${end}%, ${$theme.colors.grayScale100} ${end}%, ${
                        // @ts-ignore
                        $theme.colors.grayScale100
                      } 100%)`,
                    }
                  }
                  if (minValue >= 0) {
                    return {
                      // @ts-ignore
                      background: `linear-gradient(to right, ${$theme.colors.blackGray} 0%, ${
                        // @ts-ignore
                        $theme.colors.blackGray
                        // @ts-ignore
                      } ${((inputValue - minValue) / (maxValue - minValue)) * 100}%, ${$theme.colors.grayScale100} ${
                        ((inputValue - minValue) / (maxValue - minValue)) * 100
                      }%, ${
                        // @ts-ignore
                        $theme.colors.grayScale100
                      } 100%)`,
                    }
                  } else {
                    if (inputValue >= 0) {
                      return {
                        // @ts-ignore
                        background: `linear-gradient(to right, ${$theme.colors.blackGray} 0%, ${
                          // @ts-ignore
                          $theme.colors.blackGray
                          // @ts-ignore
                        } ${(inputValue / (maxValue - minValue)) * 100 + 50}%, ${
                          // @ts-ignore
                          $theme.colors.grayScale100
                        } ${(inputValue / (maxValue - minValue)) * 100 + 50}%, ${
                          // @ts-ignore
                          $theme.colors.grayScale100
                        } 100%)`,
                      }
                    } else {
                      return {
                        // @ts-ignore
                        background: `linear-gradient(to right, ${$theme.colors.blackGray} 0%, ${
                          // @ts-ignore
                          $theme.colors.blackGray
                          // @ts-ignore
                        } ${((maxValue + inputValue) / (maxValue - minValue)) * 100}%, ${
                          // @ts-ignore
                          $theme.colors.grayScale100
                        } ${((maxValue + inputValue) / (maxValue - minValue)) * 100}%, ${
                          // @ts-ignore
                          $theme.colors.grayScale100
                        } 100%)`,
                      }
                    }
                  }
                },
              },
              Tick: {
                style: ({ $theme }) => ({
                  display: 'none',
                }),
              },
              TickBar: {
                style: ({ $theme }) => ({
                  display: 'none',
                }),
              },
            }}
          />
        </div>
      </div>
    </ThemeProvider>
  )
}

export default SliderCustom
